import React, {useImperativeHandle} from 'react';
import ListItem from '@mui/material/ListItem';
import List from '@mui/material/List';
import ListItemText from '@mui/material/ListItemText';
import { Typography, ListItemButton, ListItemIcon, IconButton, Box, Paper, Divider } from '@mui/material';
import VerticalHamburgerMenu from '../../common/controls/VerticalHamburgerMenu';
import LoadingSpinner from '../../common/controls/LoadingSpinner';
import { useSnackbar } from '../../../context/SnackbarContext';
import { useTranslation } from 'react-i18next';
import { usePetHistories } from '../../../hooks/history/usePetHistories';
import { formatToLocalDate } from '../../../utils/dateFormatter';
import ArticleIcon from '@mui/icons-material/Article';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PatientHistoryView from '../modals/PatientHistoryView';
import { useCreateHistory } from '../../../hooks/history/useCreateHistory';
import { getHistoryById } from '../../../api/historyService';
import { useDeleteHistory } from '../../../hooks/history/useDeleteHistory';
import HistoryEntriesText from '../display/HistoryEntriesText';
import HistoryEntriesChips from '../display/HistoryEntriesChips';

export default function PatientHistoryList({ successCallback, pet = null, trigger, ref}) {

    const { t } = useTranslation('common');

    const [viewOpen, setViewOpen] = React.useState(false);

    const [selectedHistory, setSelectedHistory] = React.useState(null);

    const { histories, loading, loadMore, searchFunction, resetParent } = usePetHistories(pet.id);

    const { showSnackbar } = useSnackbar();

    const handleListItemClick = (history) => {
        setSelectedHistory(history);
        setViewOpen(true);
    };

    const onCreateHistorySuccess = async (newHistory) => {
        resetParent();
        setSelectedHistory(await getHistoryById(newHistory.id));
        setViewOpen(true);
        showSnackbar(t('history.messages.create_success'), "success");
    };

    const { create } = useCreateHistory(onCreateHistorySuccess);

    const handleCreateHistory = async () => {
        await create({pet_id: pet.id});
    };

    // Expose the function to parent
    useImperativeHandle(ref, () => ({
        handleCreateHistory
    }));


    const onDeleteHistorySuccess = () => {
        resetParent();
        showSnackbar(t('history.messages.delete_success'), "success");
    };

    const { remove } = useDeleteHistory(onDeleteHistorySuccess);

    const handleDeleteHistory = async (history) => {
        try {
            await remove(history.id);
        } 
        catch (error) {
            if (error.status === 409)
            {
                showSnackbar(t("history.messages.delete_fail_has_entries"), "error");
            }
            else {
                showSnackbar(t("history.messages.delete_fail"), "error");
            }
        }
    };

    return(
        <Paper 
          elevation={3} 
          sx={{ 
            p: 2, 
            borderRadius: 2, 
          }}
        >
        <Typography variant="h5">History</Typography>
        <br />
        {!loading && histories.data.length === 0 ? 
          <Divider variant="fullWidth" component="li">
            <Typography variant="caption" color="text.secondary">
              no medical history
            </Typography>
          </Divider>
        : null}
        <Box sx={{
          maxHeight: 300,
          overflow: "auto"
        }}>
          <List >
              {histories.data.map((history) => (
              <ListItem 
                  key={history.id} 
                  secondaryAction={
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <VerticalHamburgerMenu
                          options={[
                              {
                                  id: 1,
                                  name: 'Open',
                                  callback: () => handleListItemClick(history)
                              },
                              {
                                  id: 2,
                                  name: 'Delete',
                                  callback: () => handleDeleteHistory(history),
                                  confirmMessage: t('history.messages.delete_confirm')
                              }
                          ]}
                          />
                      </Box>
                  }
              >
                  <ListItemButton onClick={() => handleListItemClick(history)}>
                      <ListItemIcon>
                          <ArticleIcon fontSize='medium' color='primary' />
                      </ListItemIcon>
                      <ListItemText 
                          primary={
                            <>
                            <Typography variant='body1'>{formatToLocalDate(history.datetime)}</Typography>
                            <Typography sx={{m: 0}} variant='body2'>{history.case_number}</Typography>
                            </>
                          } 
                          secondary={
                            <>
                            <HistoryEntriesChips historyEntries={history.entries}/>
                            </>
                          }
                      />
                  </ListItemButton>
              </ListItem>
              ))}
          </List>
          {loading ? 
            <LoadingSpinner />
          : null }
          {/* Load More only shows if more pages exist */}
          {histories?.pagination?.page < histories?.pagination?.totalPages && (
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <IconButton 
                  onClick={loadMore}
                  sx={{
                      width: '100%',              // fill container width
                      justifyContent: 'center',   // center the icon horizontally
                      color: 'primary.main',      // use theme's primary color
                  }}
              >
                  <KeyboardArrowDownIcon />
              </IconButton>
            </Box>
          )}
        </Box>
        <PatientHistoryView 
            history={selectedHistory}
            open={viewOpen}
            onClose={() => setViewOpen(false)}
        />
        </Paper>
    );
}